import { zodResolver } from '@hookform/resolvers/zod'
import { DateFormatter, getMaxDateForAge } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { MIN_AGE_REQUIRED } from '@repo/toolkit/min-age-required'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import useTranslate from '../../../../lib/hooks/use-translate'
import { GenderEnum } from '../../../../lib/types/enums/common/gender.enum'
import { SchoolLevelEnum } from '../../../../lib/types/enums/common/school-level.enum'
import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { createDateStringSchema } from '../../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form } from '../../../elements/forms/form'
import ValidationButtons from '../../../elements/validation-buttons'
import ChildInformationEnrollmentForm from '../../enrollments/enrollment-form/child-information-enrollment-form'

export type ChildInformationInput = Pick<
  EnrollmentInput,
  | 'childGender'
  | 'childFirstName'
  | 'childLastName'
  | 'childBirthDate'
  | 'childNationality'
  | 'childNumberOfYearsInLanguage'
  | 'childEmail'
  | 'childSchoolLevel'
  | 'childPhoneNumber'
>

export const CHILD_INFORMATION_SCHEMA = (t: TFunction<AvailableLanguages, undefined>) =>
  z.object({
    childGender: createEnumSchema({ enumType: GenderEnum, fieldName: 'Genre' }),
    childFirstName: createStringSchema('Prénom'),
    childLastName: createStringSchema('NOM'),
    childBirthDate: createDateStringSchema({
      inputName: 'Date de naissance',
      maxDate: DateFormatter.toDateFormatted(getMaxDateForAge(MIN_AGE_REQUIRED))
    }),
    childSchoolLevel: createEnumSchema({
      enumType: SchoolLevelEnum,
      fieldName: 'Niveau scolaire actuel'
    }),
    childNationality: createStringSchema('Nationalité'),
    childNumberOfYearsInLanguage: createNumberSchema(`Années d'étude de la langue`),
    childEmail: createStringSchema('Email du jeune', { isEmail: true, nullish: true }),
    childPhoneNumber: PHONE_NUMBER_SCHEMA(t).nullish()
  })

type Props = {
  variant?: 'full' | 'partial'
  departureDate?: string
}

const ChildInformationForm = ({
  defaultValues,
  className,
  variant = 'full',
  departureDate,
  ...validationButtonProps
}: BaseFormProps<ChildInformationInput, true> & Props) => {
  const { t } = useTranslate()
  const form = useForm<ChildInformationInput>({
    resolver: zodResolver(CHILD_INFORMATION_SCHEMA(t)),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={className}>
          <ChildInformationEnrollmentForm variant={variant} departureDate={departureDate} />
        </div>
        <ValidationButtons hasBorderTop {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default ChildInformationForm
